import {useMemo, useRef} from 'react';
import {shallowEqual} from 'react-redux';
import {assert} from 'assert-ts';
import isEqual from 'lodash/isEqual';
import {useLocalization} from 'localization';
import {isLinkedRoleCodeListId} from 'schemaDefinition';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {selectSettingsPreferredLocale} from 'services/settings';
import {useAppSelector} from 'store/hooks';
import {getLinkedRoleCodelist} from 'schemas/links/functions';
import type {CodeList, CodeListId, CodeListMap} from '../types';
import {selectCodelistFromCache} from '../codelistSelectors';
import type {CodelistRestrictor} from '../contexts';

export function useRestrictedCodelists(
  codelistRestrictor: CodelistRestrictor,
  codelistIds: (CodeListId | LinkedRoleCodeListId)[],
): CodeListMap {
  const localization = useLocalization();
  const language = useAppSelector(selectSettingsPreferredLocale);
  const cached = useRef<CodeListMap>(undefined);

  const rawCodelists = useAppSelector(state => {
    return codelistIds.reduce<Record<string, CodeList | undefined>>(
      (acc, id) => {
        if (!isLinkedRoleCodeListId(id)) {
          acc[id] = selectCodelistFromCache(id, language, state);
        }
        return acc;
      },
      {},
    );
  }, shallowEqual);

  return useMemo(() => {
    const codelistMap = codelistIds.reduce<CodeListMap>((acc, id) => {
      if (isLinkedRoleCodeListId(id)) {
        acc[id] = getLinkedRoleCodelist(id, localization);
        return acc;
      }

      const codelist = assert(
        rawCodelists[id],
        `useRestrictedCodelists: codelist ${id} not loaded`,
      );
      acc[id] = codelistRestrictor(codelist);
      return acc;
    }, {});

    if (!isEqual(cached.current, codelistMap)) {
      cached.current = codelistMap;
    }

    return assert(cached.current);
  }, [codelistIds, codelistRestrictor, localization, rawCodelists]);
}
